import React from 'react';
import { cn } from '../../utils';

export interface LoadingContainerProps {
  /**
   * Whether the container is in a loading state
   */
  loading?: boolean;
  /**
   * Custom skeleton component to show when loading
   */
  skeleton?: React.ReactNode;
  /**
   * The content to display when not loading
   */
  children: React.ReactNode;
  /**
   * Whether to show an overlay spinner instead of skeleton
   * @default false
   */
  overlay?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Minimum height for the container when loading with overlay
   * @default "200px"
   */
  minHeight?: string;
  /**
   * Custom loading spinner for overlay mode
   */
  spinner?: React.ReactNode;
}

/**
 * LoadingContainer provides loading states for content areas
 * Based on feedback from @orchard9ai/create-vite-vike-tauri generator team
 *
 * @example
 * ```tsx
 * // With skeleton
 * <LoadingContainer
 *   loading={isLoading}
 *   skeleton={<CardSkeleton />}
 * >
 *   <Card data={data} />
 * </LoadingContainer>
 *
 * // With overlay
 * <LoadingContainer loading={isLoading} overlay>
 *   <DataTable items={items} />
 * </LoadingContainer>
 * ```
 */
export const LoadingContainer: React.FC<LoadingContainerProps> = ({
  loading = false,
  skeleton,
  children,
  overlay = false,
  className,
  minHeight = '200px',
  spinner = <span className="loading loading-spinner loading-lg" />,
}) => {
  // Show skeleton if loading and skeleton provided
  if (loading && skeleton && !overlay) {
    return <div className={cn('animate-pulse', className)}>{skeleton}</div>;
  }

  // Show overlay spinner if loading with overlay mode
  return (
    <div
      className={cn('relative', className)}
      style={loading && overlay ? { minHeight } : undefined}
      aria-busy={loading}
    >
      {children}
      {loading && overlay && (
        <div className="absolute inset-0 bg-base-100/80 backdrop-blur-sm flex items-center justify-center z-10 focus-container rounded-inherit">
          <div className="flex flex-col items-center gap-4">
            {spinner}
            <span className="text-sm text-base-content/70">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
};
